import { Lock, Crown, Zap, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Universal paywall modal. Shows £9 lifetime + £15 monthly options.
 *  Logs paywall hit on open via /api/paywall/hit. */
export default function PaywallModal({
  open,
  onOpenChange,
  kind = "locked_feature_click",
  title = "Pro feature",
  body = "This is locked behind Zuntria Pro. Unlock it instantly below.",
  context = null,
  highlights = null,
}) {
  useEffect(() => {
    if (open) api.post("/paywall/hit", { kind, context }).catch(() => {});
  }, [open, kind, context]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="paywall-modal">
        <div className="flex items-center gap-2 text-amber-400">
          <Lock className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Pro feature</span>
        </div>
        <DialogTitle className="mt-3 font-display text-2xl font-normal">{title}</DialogTitle>
        <DialogDescription className="mt-2 text-sm text-muted-foreground">{body}</DialogDescription>
        {highlights?.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="paywall-highlights">
            {highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-sm">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" /> {h}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 grid grid-cols-1 gap-3">
          <Link
            to="/pricing?founding=1"
            onClick={() => onOpenChange?.(false)}
            data-testid="paywall-cta-lifetime"
          >
            <Button className="h-12 w-full bg-orange-500 text-white hover:bg-orange-600">
              <Crown className="mr-2 h-4 w-4" /> £9 Lifetime Pro — founding member
            </Button>
          </Link>
          <Link
            to="/pricing"
            onClick={() => onOpenChange?.(false)}
            data-testid="paywall-cta-monthly"
          >
            <Button variant="outline" className="h-12 w-full">
              <Zap className="mr-2 h-4 w-4" /> £14.99/month — cancel anytime
            </Button>
          </Link>
        </div>
        <OneTimeUnlock onDone={() => onOpenChange?.(false)} />
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          Secure PayPal checkout · Instant access
        </p>
      </DialogContent>
    </Dialog>
  );
}

function OneTimeUnlock({ onDone }) {
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    setBusy(true);
    try {
      await api.post("/upgrades/assistants-unlock");
      toast.success("Unlocked! All 15 assistants + rent-collection tools are yours forever 🎉");
      onDone?.();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl border border-primary/40 bg-primary/5 p-4" data-testid="paywall-onetime-unlock">
      <div className="flex items-center gap-2 text-primary">
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wider">One-time · No subscription</span>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Unlock all 15 AI assistants + rent-collection updates forever — one payment of £14.99 from your wallet.
      </p>
      <Button onClick={unlock} disabled={busy} variant="outline" size="sm" className="mt-3 w-full border-primary/50" data-testid="paywall-onetime-unlock-btn">
        {busy ? "Unlocking…" : "Unlock for £14.99 — one time"}
      </Button>
    </div>
  );
}
